/**
 * Staff Portal v2 — shared notification unread-count state.
 *
 * A tiny context so the shell header badge, the dashboard notifications
 * panel, and the /notifications page share the same unread count without
 * every consumer polling independently. Fixes the "mark read on the
 * notifications page → header badge doesn't update" gap.
 *
 * Contract:
 *   • Providers seed the unread count from the dashboard aggregate
 *     (single source of truth for first load).
 *   • Consumers can:
 *       - read `unreadCount`
 *       - call `setUnreadCount(n)` for a hard override (e.g. after a
 *         refresh returns an authoritative number)
 *       - call `decrement(n)` after marking one/many as read locally
 *       - call `reset()` to zero (mark-all-read)
 *   • Optimistic updates: consumers should call `decrement`/`reset`
 *     BEFORE the network call and pass `rollback` to restore on failure.
 *   • Cleared on logout via `clearStaffV2NotificationState()` — mirrors
 *     the flag-cache pattern.
 *
 * No participant clinical detail is stored here — only a single number.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const StaffNotificationsContext = createContext(null);

let _snapshot = { unreadCount: 0 };
export function clearStaffV2NotificationState() {
  _snapshot = { unreadCount: 0 };
}

export function StaffNotificationsProvider({ children, initialUnread = 0 }) {
  const [unreadCount, setUnreadCountState] = useState(initialUnread);

  const setUnreadCount = useCallback((n) => {
    const safe = Math.max(0, Number(n) || 0);
    _snapshot.unreadCount = safe;
    setUnreadCountState(safe);
  }, []);

  const decrement = useCallback((n = 1) => {
    setUnreadCountState((prev) => {
      const next = Math.max(0, prev - Math.max(0, n));
      _snapshot.unreadCount = next;
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    _snapshot.unreadCount = 0;
    setUnreadCountState(0);
  }, []);

  const value = useMemo(() => ({
    unreadCount, setUnreadCount, decrement, reset,
  }), [unreadCount, setUnreadCount, decrement, reset]);

  return (
    <StaffNotificationsContext.Provider value={value}>
      {children}
    </StaffNotificationsContext.Provider>
  );
}

export function useStaffNotifications() {
  const ctx = useContext(StaffNotificationsContext);
  if (!ctx) {
    // Fallback so callers outside a provider don't crash — they get
    // read-only zero + no-op setters, matching the fail-closed pattern.
    return {
      unreadCount: 0,
      setUnreadCount: () => {},
      decrement: () => {},
      reset: () => {},
    };
  }
  return ctx;
}

export default StaffNotificationsContext;
